import { ColumnsPanelTrigger, Toolbar } from '@mui/x-data-grid-pro';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DispatchBreadcrumbs from './DispatchBreadcrumbs';
import QuerySelector from '@dispatchBoard/QuerySelector';
import { useFetchBoardQuery } from '@api/Trinium/Endpoints/BoardQueries/slice';
import { defaultAllOpenQuery, isActiveQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import RowActions from '../../DataGrid/Dispatch/Cells/RowActions';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import useSearchParameter from '@hooks/useSearchParameter';
import { useAppSelector } from '@reduxStore';
import RefreshButton from './Actions/RefreshButton';
import SetDefaultQueryButton from './Actions/SetDefaultQueryButton';
import ExportButton from './Actions/ExportButton';
import MobileFilterMenu from '../MobileFilterMenu';
import { StyledQuickFilter } from '../StyledComponents';
import CreateOrder from './Actions/CreateOrder';
import EditBoardQueriesButton from './Actions/EditBoardQueriesButton';
import QuickFilterOperatorDropdown from '../QuickFilterOperatorDropdown';

import styles from './DataGrid.Dispatch.module.css';
import DensityMenuButton from '../ToolbarItems/DensityMenu';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';

/**
 * Toolbar that sits on top of the Dispatch Board and displays different actions available.
 */
export default function DispatchGridToolbar() {
  const { selectedTerminal, selectedBoardQueryName } = useAppSelector((state) => state.applicationState);
  const terminalNumber = String(selectedTerminal?.number);
  const { data: user } = useFetchMyProfileQuery(undefined);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { currentQuery, defaultQuery } = useFetchBoardQuery(terminalNumber, {
    selectFromResult: ({ data }) => ({
      currentQuery: data?.find((q) => q.name === selectedBoardQueryName && isActiveQuery(q)) ?? defaultAllOpenQuery,
      defaultQuery: data?.find((q) => q.isDefault && isActiveQuery(q))
    })
  });

  /** Job ID Query Parameter used as a rendering flag. */
  const jobId = useSearchParameter('job');

  if (isMobile) {
    return (
      <Toolbar className={styles.toolbar}>
        <div className={styles['mobile-action-menu']}>
          <RowActions />
        </div>

        {jobId ? (
          <DispatchBreadcrumbs />
        ) : (
          <>
            <QuerySelector
              terminalNumber={terminalNumber}
              currentQuery={currentQuery}
            />

            <RefreshButton terminalNumber={terminalNumber} />

            <CreateOrder />

            <SetDefaultQueryButton
              terminalNumber={terminalNumber}
              currentQuery={currentQuery}
              defaultQuery={defaultQuery}
            />
          </>
        )}

        <div className={styles['right-justified']}>
          <div className={styles['mobile-action-menu']}>
            <MobileFilterMenu />
          </div>

          <ExportButton name={selectedBoardQueryName} />

          <StyledQuickFilter
            className={styles['quick-filter']}
            placeholder='Filter by Keyword'
            debounceMs={300}
            autoFocus
          />

          <QuickFilterOperatorDropdown className={styles['hidden']} />
        </div>
      </Toolbar>
    );
  }

  return (
    <Toolbar className={styles.toolbar}>
      {jobId ? (
        <DispatchBreadcrumbs />
      ) : (
        <>
          <QuerySelector
            terminalNumber={terminalNumber}
            currentQuery={currentQuery}
          />

          {user?.canModifyBoardQueries && (
            <EditBoardQueriesButton
              terminalNumber={terminalNumber}
              queryName={currentQuery.name}
            />
          )}

          <RefreshButton terminalNumber={terminalNumber} />

          <CreateOrder />

          <SetDefaultQueryButton
            terminalNumber={terminalNumber}
            currentQuery={currentQuery}
            defaultQuery={defaultQuery}
          />
        </>
      )}

      <div className={styles['right-justified']}>
        {/* className and title belong on the trigger — it clones the rendered element and overwrites its own props */}
        <ColumnsPanelTrigger
          title='Show/Hide Columns'
          className={`${styles['icon-btn']} ${styles['columns-btn']}`}
          render={<IconButton />}>
          <ViewColumnIcon />
        </ColumnsPanelTrigger>

        <DensityMenuButton />

        <ExportButton name={selectedBoardQueryName} />

        <StyledQuickFilter
          className={styles['quick-filter']}
          placeholder='Filter by Keyword'
          debounceMs={300}
          autoFocus
        />

        <QuickFilterOperatorDropdown />
      </div>
    </Toolbar>
  );
}
